<template>
  <button-collapse
    :title="isToken ? 'Token Authentication' : `${typeName} Restrictions`"
    :collapse="hideRestrictionType"
    :disable-save="disableSave"
    @show="hideRestrictionType = false"
    @cancel="cancel"
    @save="save"
  >
    <template #subtitle>
      <div class="d-flex align-items-center">
        <span class="subtitle">
          {{ type.id !== 0 ? 'Enabled.' : 'Disabled' }}
          <span
            v-if="type.restrictionMode && !isToken"
            class="text-secondary"
          >
            {{ type.restrictionMode }}
          </span>
        </span>
        <b-spinner
          v-if="fetchingRestriction || (isGeo && fetchingCountries)"
          small
          class="ml-1"
        />
      </div>
    </template>

    <template #body>
      <div
        v-if="restriction && (isGeo ? countries.length > 0 : true) && !isToken"
        class="col-md-8 col-lg-6 p-0"
      >
        <b-form-group
          v-slot="{ ariaDescribedby }"
          label="Restriction Mode"
        >
          <b-form-radio-group
            :id="`restriction-mode-options-${typeName}`"
            v-model="restriction.restrictionMode"
            :options="restrictionModeOptions"
            :aria-describedby="ariaDescribedby"
            :name="`restriction-mode-options-${typeName}`"
            stacked
          />
        </b-form-group>
        <div v-if="restriction.restrictionMode !== null">
          <div v-if="!isGeo">
            <validation-provider
              #default="{ errors }"
              :name="isIp ? 'IP' : 'Referer'"
              :rules="isIp ? `ipWithSubnet` : `url`"
              class="d-flex align-items-center"
            >
              <b-form-input
                v-model="restrictionContainerInput"
                :placeholder="`Enter ${typeName} here`"
                class="my-0"
                type="text"
                @keyup.enter="
                  errors.length > 0 || restrictionContainerInput.length === 0
                    ? ''
                    : addRestrictionContainer()
                "
              />
              <b-button
                variant="bg-primary"
                :disabled="
                  errors.length > 0 || restrictionContainerInput.length === 0
                "
                @click="addRestrictionContainer"
              >
                <GjIcon
                  name="Plus"
                  size="20"
                  class="icon-left"
                />
                Add
              </b-button>
            </validation-provider>
          </div>
          <div v-else>
            <v-select
              v-if="countries.length > 0"
              v-model="restriction.restrictionContainer"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              multiple
              label="title"
              :options="countries"
              placeholder="Select countries here..."
            />
          </div>
          <div class="d-flex align-items-center mt-1">
            <template v-if="!isGeo">
              <div
                v-for="restrictionValue in restriction.restrictionContainer"
                :key="restrictionValue"
                class="rounded bg-primary text-white border-default border-t border restriction-container-item mr-1"
              >
                <span>
                  {{ restrictionValue }}
                </span>
                <span
                  class="cursor-pointer"
                  @click="removeRestrictionContainer(restrictionValue)"
                >
                  <GjIcon
                    name="Close"
                    size="17"
                  />
                </span>
              </div>
            </template>
            <div
              v-if="restriction.restrictionContainer.length > 0"
              class="rounded border-danger border restriction-container-item text-danger mr-1 cursor-pointer"
              @click="clearRestrictionContainer"
            >
              <span>
                Clear
              </span>
            </div>
            <div
              v-else
              class="text-secondary text-sm"
            >
              No
              {{
                isGeo ? 'countries' : type.restrictionType.toLowerCase() + 's'
              }}
              added yet. Start by adding from the input field above.
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="col-md-8 col-lg-6 p-0"
      >
        <b-form-group>
          <template #label>
            Restriction Mode
          </template>
          <b-form-radio-group
            :id="`restriction-mode-options-${typeName}`"
            v-model="tokenRestriction"
            :options="tokenRestrictionOptions"
            :name="`restriction-mode-options-${typeName}`"
            stacked
          />
        </b-form-group>
        <a
          v-if="isToken"
          :href="tokenAuthenticationLink"
          target="_blank"
        >
          Need help with token authentication
          <GjIcon name="Question" />
        </a>
        <b-button
          v-if="restriction && tokenRestriction && !!restriction.secretKey"
          variant="bg-white"
          @click="showAccessToken = true"
        >
          <GjIcon
            name="InfoAlt"
            class="icon-left"
          />
          Get Secret Key
        </b-button>
      </div>
      <template v-if="isToken && tokenRestriction">
        <b-modal
          v-model="showAccessToken"
          title="Token"
          centered
          ok-title="Close"
          ok-variant="bg-white"
          ok-only
        >
          <input-field
            name="restriction-token"
            label="Secret Key"
            label-position="top"
          >
            <b-input-group>
              <template #append>
                <b-button
                  v-clipboard:copy="restriction.secretKey"
                  v-clipboard:success="copySuccess"
                  variant="bg-gray"
                  class="border-left-0"
                >
                  <span>
                    <GjIcon name="Copy" />
                  </span>
                </b-button>
              </template>
              <b-form-input
                id="restriction-token"
                :value="restriction.secretKey"
                class="mr-0"
                readonly
              />
            </b-input-group>
          </input-field>
        </b-modal>
      </template>
    </template>
  </button-collapse>
</template>

<script>
import ButtonCollapse from '@/components/button/ButtonCollapse.vue'
import helpers from '@/mixins/helpers'
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'
import { url, ipWithSubnet } from '@validations'
import { isEqual } from 'lodash'

export default {
  name: 'RestrictionType',
  components: {
    ButtonCollapse,
    vSelect,
    ValidationProvider,
  },
  mixins: [helpers],
  props: {
    type: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      url,
      ipWithSubnet,
      restrictionContainerInput: '',
      hideRestrictionType: true,
      typeInitialized: false,
      restriction: null,
      fetchingRestriction: false,
      tokenRestriction: false,
      tokenRestrictionOptions: [
        {
          text: 'Enabled',
          value: true,
        },
        {
          text: 'Disabled',
          value: false,
        },
      ],
      showAccessToken: false,
      initial: {},
      initialTokenRestriction: null,
    }
  },
  computed: {
    disableSave() {
      if (this.restriction) {
        const final = this.restriction.restrictionMode !== null
          ? this.restriction.restrictionContainer.length === 0
          : false
        if (this.isToken) {
          return this.tokenRestriction === !!this.restriction.secretKey
        }
        if (this.restriction.restrictionMode !== this.initial.restrictionMode) {
          return final
        }
        if (
          !isEqual(
            this.restriction.restrictionContainer,
            this.initial.restrictionContainer,
          )
        ) {
          return final
        }
        return true
      }
      return true
    },
    distributionId() {
      return this.$route.params.id
    },
    restrictionModeOptions() {
      if (this.restriction) {
        return [
          ...this.getObjectOptions(this.restriction.restrictionModeOptions),
          { text: 'No Restriction', value: null },
        ]
      }
      return []
    },
    countries() {
      return this.$store.state.organization.countries
        ? this.$store.state.organization.countries.map(country => country.name)
        : []
    },
    fetchingCountries() {
      return this.$store.state.organization.fetchingCountries
    },
    isIp() {
      return this.type.restrictionType.toLowerCase() === 'ip'
    },
    isGeo() {
      return this.type.restrictionType.toLowerCase() === 'geo'
    },
    isReferer() {
      return this.type.restrictionType.toLowerCase() === 'referer'
    },
    isToken() {
      return this.type.restrictionType.toLowerCase() === 'token'
    },
    containerInputValid() {
      if (this.isIp) {
        return true
      }
      if (this.isReferer) {
        return true
      }
      return true
    },
    restrictionTypeOnCreate() {
      if (!this.restriction) return null
      if (this.isIp) return this.restriction.restrictionTypeOptions.Ip
      if (this.isGeo) return this.restriction.restrictionTypeOptions.Geo
      if (this.isToken) return this.restriction.restrictionTypeOptions.Token
      return this.restriction.restrictionTypeOptions.Referer
    },
    typeName() {
      if (this.isIp) return 'IP'
      if (this.isGeo) return 'Geo'
      if (this.isReferer) return 'Referer'
      if (this.isToken) return 'Token'
      return ''
    },
    tokenAuthenticationLink() {
      return process.env.NODE_ENV === 'development'
        || process.env.VUE_APP_ENV === 'staging'
        ? 'https://captain-docs.gjirafa.dev/cdn/user-guide/working-with-token'
        : 'https://docs.captain.gjirafa.tech/cdn/user-guide/working-with-token'
    },
  },
  watch: {
    hideRestrictionType(val) {
      if (!val) {
        if (this.isGeo && this.countries.length === 0) {
          this.fetchCountries()
        }
        this.fetchRestrictionById()
        this.typeInitialized = true
      }
    },
  },
  methods: {
    cancel() {
      this.hideRestrictionType = true
    },
    save() {
      if (!this.isToken) {
        if (this.restriction.restrictionMode !== null) {
          if (this.type.id === 0) {
            // create
            this.$store
              .dispatch('cdn/createRestriction', {
                distributionId: this.distributionId,
                restriction: {
                  restrictionMode: this.restriction.restrictionMode,
                  restrictionType: this.restrictionTypeOnCreate,
                  restrictionContainer: [
                    ...this.restriction.restrictionContainer,
                  ],
                },
              })
              .then(() => {
                this.successToast('Restriction saved successfully')
              })
              .catch(error => {
                this.errorToast(
                  error.response?.data?.errors[0] || 'Error saving restriction',
                )
              })
              .finally(() => {
                this.emitRefresh()
                this.hideRestrictionType = true
              })
          } else {
            // update
            this.$store
              .dispatch('cdn/updateRestriction', {
                distributionId: this.distributionId,
                restrictionId: this.restriction.id,
                restriction: {
                  restrictionMode: this.restriction.restrictionMode,
                  restrictionContainer: [
                    ...this.restriction.restrictionContainer,
                  ],
                },
              })
              .then(() => {
                this.successToast('Restriction saved successfully')
              })
              .catch(error => {
                this.errorToast(
                  error.response?.data?.errors[0] || 'Error saving restriction',
                )
              })
              .finally(() => {
                this.emitRefresh()
                this.hideRestrictionType = true
              })
          }
        } else {
          // delete
          this.$store
            .dispatch('cdn/deleteRestriction', {
              distributionId: this.distributionId,
              restrictionId: this.restriction.id,
            })
            .then(() => {
              this.successToast('Restriction disabled successfully')
            })
            .catch(error => {
              this.errorToast(
                error.response?.data?.errors[0]
                  || 'Error disabling restriction',
              )
            })
            .finally(() => {
              this.emitRefresh()
              this.hideRestrictionType = true
            })
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (this.tokenRestriction) {
          this.$store
            .dispatch('cdn/createRestriction', {
              distributionId: this.distributionId,
              restriction: {
                restrictionType: this.restrictionTypeOnCreate,
                restrictionMode: 0,
                restrictionContainer: [
                  ...this.restriction.restrictionContainer,
                ],
              },
            })
            .then(res => {
              if (res.data.result) {
                this.fetchRestrictionById(res.data.result.id)
              } else {
                this.hideRestrictionType = true
              }
              this.successToast('Restriction saved successfully')
            })
            .catch(error => {
              this.errorToast(
                error.response?.data?.errors[0] || 'Error saving restriction',
              )
            })
            .finally(() => {
              this.emitRefresh()
            })
        } else {
          this.$store
            .dispatch('cdn/deleteRestriction', {
              distributionId: this.distributionId,
              restrictionId: this.restriction.id,
            })
            .then(() => {
              this.successToast('Restriction disabled successfully')
            })
            .catch(error => {
              this.errorToast(
                error.response?.data?.errors[0]
                  || 'Error disabling restriction',
              )
            })
            .finally(() => {
              this.emitRefresh()
              this.hideRestrictionType = true
            })
        }
      }
    },
    emitRefresh() {
      this.$emit('refreshRestrictions')
    },
    fetchRestrictionById(manualId) {
      this.fetchingRestriction = true
      this.$store
        .dispatch('cdn/fetchRestrictionById', {
          distributionId: this.distributionId,
          restrictionId: manualId || this.type.id,
        })
        .then(res => {
          this.restriction = { ...res.data.result }
          if (this.type.id === 0) {
            this.restriction.restrictionMode = null
          }
          if (this.isToken) {
            this.tokenRestriction = !!this.restriction.secretKey
            this.initialTokenRestriction = this.tokenRestriction
          }
          this.initial = { ...this.restriction }
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0] || 'Error fetching restriction',
          )
        })
        .finally(() => {
          this.fetchingRestriction = false
        })
    },
    fetchCountries() {
      this.$store.dispatch('organization/fetchCountries').catch(error => {
        this.errorToast(
          error.response?.data?.errors[0] || 'Something went wrong while fetching countries',
        )
      })
    },
    addRestrictionContainer() {
      if (
        this.restrictionContainerInput
        && !this.restriction.restrictionContainer.includes(
          this.restrictionContainerInput,
        )
      ) {
        this.restriction.restrictionContainer.push(
          this.restrictionContainerInput,
        )
      }
      this.restrictionContainerInput = ''
    },
    removeRestrictionContainer(value) {
      this.restriction.restrictionContainer = this.restriction.restrictionContainer.filter(
        el => el !== value,
      )
    },
    clearRestrictionContainer() {
      this.restriction.restrictionContainer = []
    },
  },
}
</script>

<style lang="scss" scoped>
.custom-control.custom-radio {
  margin-bottom: 8px;
}
.restriction-container-item {
  padding: 4px 7px;
  font-size: 0.9rem;
}
</style>
